import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowLeft,
  Upload,
  Download,
  FileJson,
  AlertCircle,
  CheckCircle2,
  Users,
  FileSpreadsheet,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ImportExport() {
  const navigate = useNavigate();
  const [importData, setImportData] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [importMode, setImportMode] = useState('csv'); // 'csv' or 'json'

  const sampleData = {
    users: [
      {
        email: "cliente@exemplo.com",
        name: "Nome do Cliente",
        phone: "+351 912 345 678",
        address: "Morada completa",
        iban: "PT50 0000 0000 0000 0000 0000 0",
        profit_percentage: 8.6,
        balance: 50000.00,
        accumulated_returns: 4300.00,
        password: "senha123"
      }
    ]
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('O ficheiro CSV não contém dados suficientes');
    }

    // Parse header
    const headerLine = lines[0];
    const headers = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < headerLine.length; i++) {
      const char = headerLine[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        headers.push(currentField.trim().toLowerCase().replace(/"/g, ''));
        currentField = '';
      } else {
        currentField += char;
      }
    }
    headers.push(currentField.trim().toLowerCase().replace(/"/g, ''));

    // Find column indices
    const userIdIdx = headers.findIndex(h => h.includes('user id'));
    const userNameIdx = headers.findIndex(h => h.includes('user name'));
    const userEmailIdx = headers.findIndex(h => h.includes('user email') || h.includes('email'));
    const amountIdx = headers.findIndex(h => h === 'amount' || h.includes('amount'));
    const typeIdx = headers.findIndex(h => h.includes('transaction type') || h.includes('type'));
    const methodIdx = headers.findIndex(h => h.includes('payment method') || h.includes('method'));
    const transIdIdx = headers.findIndex(h => h.includes('transaction id'));

    if (userEmailIdx === -1 || amountIdx === -1 || typeIdx === -1) {
      throw new Error('O ficheiro CSV não contém as colunas necessárias (email, amount, transaction type)');
    }

    // Parse data rows
    const transactions = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = [];
      currentField = '';
      inQuotes = false;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(currentField.trim().replace(/"/g, ''));
          currentField = '';
        } else {
          currentField += char;
        }
      }
      values.push(currentField.trim().replace(/"/g, ''));

      const email = values[userEmailIdx];
      if (!email || !email.includes('@')) continue;

      const amount = parseFloat(values[amountIdx]) || 0;
      if (amount === 0) continue;

      transactions.push({
        user_id: values[userIdIdx] || '',
        user_name: values[userNameIdx] || email.split('@')[0],
        user_email: email.toLowerCase(),
        amount: amount,
        transaction_type: values[typeIdx] || 'Depósito',
        payment_method: methodIdx >= 0 ? values[methodIdx] || '' : '',
        transaction_id: transIdIdx >= 0 ? values[transIdIdx] || '' : String(Date.now())
      });
    }

    return transactions;
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      toast.error('Cole os dados para importar');
      return;
    }

    setImportLoading(true);
    setImportResult(null);

    try {
      if (importMode === 'csv') {
        // Parse CSV and send to new endpoint
        const transactions = parseCSV(importData);
        
        if (transactions.length === 0) {
          throw new Error('Nenhuma transação válida encontrada no ficheiro');
        }

        toast.info(`A processar ${transactions.length} transações...`);

        const response = await axios.post(`${API_URL}/admin/import-csv`, { transactions });
        setImportResult({
          ...response.data,
          mode: 'csv'
        });
        
        if (response.data.imported_users > 0 || response.data.updated_users > 0) {
          toast.success(`Importação concluída! ${response.data.imported_users} novos utilizadores, ${response.data.updated_users} atualizados`);
        }
      } else {
        // JSON mode - existing functionality
        let parsedData;
        try {
          parsedData = JSON.parse(importData);
          if (!parsedData.users || !Array.isArray(parsedData.users)) {
            throw new Error('Formato inválido');
          }
        } catch (e) {
          toast.error('JSON inválido. Verifique o formato dos dados.');
          return;
        }

        const response = await axios.post(`${API_URL}/admin/import-users`, parsedData);
        setImportResult({
          ...response.data,
          mode: 'json'
        });
        if (response.data.imported > 0) {
          toast.success(`${response.data.imported} utilizadores importados com sucesso!`);
        }
      }
    } catch (error) {
      // Error silenced: 'Import error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Erro ao importar dados');
    } finally {
      setImportLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/export-users`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sgfi_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Backup exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar dados');
    } finally {
      setExportLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImportData(event.target.result);
        // Auto-detect mode based on file extension
        if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
          setImportMode('csv');
        } else if (file.name.endsWith('.json')) {
          setImportMode('json');
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="import-export-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Importar / Exportar Dados</h1>
          <p className="text-slate-400 text-sm">Migrar dados do WordPress/WooCommerce ou fazer backup</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Import Section */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-400" />
            Importar Clientes
          </h2>

          <div className="space-y-4">
            {/* Import Mode Toggle */}
            <div className="flex gap-2 p-1 bg-white/5 rounded-xl">
              <button
                onClick={() => setImportMode('csv')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  importMode === 'csv' 
                    ? 'bg-blue-500 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <FileText className="w-4 h-4" />
                CSV / TXT
              </button>
              <button
                onClick={() => setImportMode('json')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  importMode === 'json' 
                    ? 'bg-blue-500 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <FileJson className="w-4 h-4" />
                JSON
              </button>
            </div>

            {/* Instructions based on mode */}
            {importMode === 'csv' ? (
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <h3 className="text-blue-400 font-medium mb-2">Formato CSV do WordPress:</h3>
                <p className="text-sm text-slate-300 mb-2">
                  O ficheiro deve ter as colunas: User Email, Amount, Transaction Type
                </p>
                <p className="text-xs text-slate-400">
                  Tipos suportados: Depósito, Investimento, Lucros, Levantamento, Bónus, etc.
                </p>
              </div>
            ) : (
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <h3 className="text-blue-400 font-medium mb-2">Formato JSON:</h3>
                <pre className="p-3 bg-[#161C22] rounded-xl text-xs text-slate-400 overflow-x-auto">
{JSON.stringify(sampleData, null, 2)}
                </pre>
              </div>
            )}

            {/* File Upload */}
            <div>
              <Label className="text-slate-300 mb-2 block">
                Carregar ficheiro {importMode === 'csv' ? 'CSV/TXT' : 'JSON'}:
              </Label>
              <Input
                type="file"
                accept={importMode === 'csv' ? '.csv,.txt' : '.json'}
                onChange={handleFileUpload}
                className="bg-[#161C22] border-white/10 text-white file:bg-blue-500 file:text-white file:border-0 file:rounded-lg file:px-4 file:py-2 file:mr-4 file:cursor-pointer"
                data-testid="file-upload"
              />
            </div>

            {/* Text Input */}
            <div>
              <Label className="text-slate-300 mb-2 block">Ou cole os dados diretamente:</Label>
              <Textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder={importMode === 'csv' ? '"User Id","User Name","User Email",Amount,...' : '{"users": [...]}'}
                className="h-40 bg-[#161C22] border-white/10 text-white font-mono text-sm rounded-xl"
                data-testid="import-data"
              />
            </div>

            {/* Import Button */}
            <Button
              onClick={handleImport}
              disabled={importLoading || !importData.trim()}
              className="w-full bg-blue-500 hover:bg-blue-600 rounded-xl"
              data-testid="import-btn"
            >
              {importLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  A importar...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Importar {importMode === 'csv' ? 'CSV' : 'JSON'}
                </div>
              )}
            </Button>

            {/* Import Result */}
            {importResult && (
              <div className={`p-4 rounded-xl ${
                (importResult.imported > 0 || importResult.imported_users > 0 || importResult.updated_users > 0)
                  ? 'bg-emerald-500/10 border border-emerald-500/20' 
                  : 'bg-amber-500/10 border border-amber-500/20'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {(importResult.imported > 0 || importResult.imported_users > 0 || importResult.updated_users > 0) ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                  )}
                  <span className="font-medium text-white">Resultado da Importação</span>
                </div>
                <div className="text-sm space-y-1">
                  {importResult.mode === 'csv' ? (
                    <>
                      <p className="text-emerald-400">✓ Novos utilizadores: {importResult.imported_users}</p>
                      <p className="text-blue-400">↻ Utilizadores atualizados: {importResult.updated_users}</p>
                      <p className="text-slate-400">Transações importadas: {importResult.imported_transactions}</p>
                      <p className="text-amber-400">⊘ Transações ignoradas: {importResult.skipped_transactions}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-emerald-400">✓ Importados: {importResult.imported}</p>
                      <p className="text-amber-400">⊘ Ignorados: {importResult.skipped}</p>
                      <p className="text-slate-400">Total: {importResult.total}</p>
                    </>
                  )}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      <p className="text-red-400 text-xs mb-1">Erros/Avisos:</p>
                      <div className="max-h-32 overflow-y-auto">
                        {importResult.errors.slice(0, 10).map((err, i) => (
                          <p key={i} className="text-xs text-slate-500">{err}</p>
                        ))}
                        {importResult.errors.length > 10 && (
                          <p className="text-xs text-slate-500">... e mais {importResult.errors.length - 10} erros</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Export Section */}
        <div className="space-y-6">
          <div className="glass-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-emerald-400" />
              Exportar Backup
            </h2>

            <p className="text-slate-400 text-sm mb-4">
              Faça download de todos os dados dos clientes em formato JSON para backup ou migração.
            </p>

            <Button
              onClick={handleExport}
              disabled={exportLoading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
              data-testid="export-btn"
            >
              {exportLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  A exportar...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Exportar Todos os Dados
                </div>
              )}
            </Button>
          </div>

          {/* WordPress Migration Guide */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-400" />
              Guia de Migração WordPress
            </h2>

            <div className="space-y-4 text-sm">
              <div className="p-3 bg-white/5 rounded-xl">
                <h3 className="text-white font-medium mb-2">1. Exportar do WooCommerce</h3>
                <p className="text-slate-400">
                  No WordPress, vá a WooCommerce → Relatórios → Exportar ou use o plugin 
                  "WP All Export" para exportar os dados de transações de carteira.
                </p>
              </div>

              <div className="p-3 bg-white/5 rounded-xl">
                <h3 className="text-white font-medium mb-2">2. Formato do Ficheiro</h3>
                <p className="text-slate-400">
                  O ficheiro CSV deve ter as colunas: User Email, Amount, Transaction Type.
                  Colunas opcionais: User Name, User Id, Transaction Id.
                </p>
              </div>

              <div className="p-3 bg-white/5 rounded-xl">
                <h3 className="text-white font-medium mb-2">3. Tipos de Transação</h3>
                <p className="text-slate-400">
                  <span className="text-emerald-400">Créditos:</span> Depósito, Investimento, Bónus, Oferta, Prémio<br/>
                  <span className="text-red-400">Débitos:</span> Levantamento, Saque<br/>
                  <span className="text-blue-400">Lucros:</span> Lucros, Juros
                </p>
              </div>

              <div className="p-3 bg-white/5 rounded-xl">
                <h3 className="text-white font-medium mb-2">4. Após a Importação</h3>
                <p className="text-slate-400">
                  As senhas são geradas automaticamente e guardadas nas notas do admin.
                  Pode ver as senhas em Utilizadores → [Nome] → Editar → Notas Admin.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
